import React, { useState, useEffect } from 'react';
import { Trophy, Crown, Zap, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';

const StandingsComparison = ({ 
  finalData, 
  manager1Name, 
  manager2Name, 
  yearRange,
  className = "" 
}) => {
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: null });
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  
  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Hide scroll indicator after user interaction
  useEffect(() => {
    if (isMobile && showScrollIndicator) {
      const timer = setTimeout(() => {
        setShowScrollIndicator(false);
      }, 3000); // Hide after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [isMobile, showScrollIndicator]);
  
  // Process standings data for visualization
  const processStandingsData = () => {
    if (!finalData || !finalData.length) return { chartData: [], comparison: {}, streaks: {} };

    // Get all years in the range
    const years = [];
    for (let year = yearRange[0]; year <= yearRange[1]; year++) {
      years.push(year);
    }

    // Filter data within the year range
    const filteredData = finalData.filter(row => 
      row.Year >= yearRange[0] && 
      row.Year <= yearRange[1]
    );

    // Create chart data by year
    const chartData = years.map(year => {
      const yearData = filteredData.filter(row => row.Year === year);
      
      // Create array of 12 positions (1-12)
      const positions = Array.from({ length: 12 }, (_, i) => {
        const position = i + 1;
        const managerAtPosition = yearData.find(row => row.Standings === position);
        
        return {
          position,
          manager: managerAtPosition?.Owner || null,
          isManager1: managerAtPosition?.Owner === manager1Name,
          isManager2: managerAtPosition?.Owner === manager2Name,
          isSelected: managerAtPosition?.Owner === manager1Name || managerAtPosition?.Owner === manager2Name
        };
      });

      return {
        year,
        positions
      };
    }).filter(yearData => yearData.positions.some(p => p.manager)); // Only include years with data

    // Calculate head-to-head comparison
    let manager1Better = 0;
    let manager2Better = 0;

    chartData.forEach(yearData => {
      const manager1Position = yearData.positions.find(p => p.isManager1)?.position;
      const manager2Position = yearData.positions.find(p => p.isManager2)?.position;
      
      if (manager1Position && manager2Position) {
        if (manager1Position < manager2Position) {
          manager1Better++;
        } else if (manager2Position < manager1Position) {
          manager2Better++;
        }
      }
    });

    // Calculate streaks
    const calculateStreaks = () => {
      // Get yearly finishes for both managers
      const manager1Finishes = [];
      const manager2Finishes = [];
      
      chartData.forEach(yearData => {
        const manager1Position = yearData.positions.find(p => p.isManager1)?.position;
        const manager2Position = yearData.positions.find(p => p.isManager2)?.position;
        
        if (manager1Position && manager2Position) {
          manager1Finishes.push({
            year: yearData.year,
            position: manager1Position,
            betterThanOther: manager1Position < manager2Position
          });
          manager2Finishes.push({
            year: yearData.year,
            position: manager2Position,
            betterThanOther: manager2Position < manager1Position
          });
        }
      });

      // Calculate current and longest streaks for each manager
      const calculateManagerStreaks = (finishes, managerName) => {
        let currentStreak = 0;
        let longestStreak = 0;
        let tempStreak = 0;
        let currentStreakType = null;
        
        // Sort by year to ensure chronological order
        const sortedFinishes = [...finishes].sort((a, b) => a.year - b.year);
        
        // Calculate longest streak (historical)
        for (let i = 0; i < sortedFinishes.length; i++) {
          if (sortedFinishes[i].betterThanOther) {
            tempStreak++;
            longestStreak = Math.max(longestStreak, tempStreak);
          } else {
            tempStreak = 0;
          }
        }
        
        // Calculate current streak (from most recent years backwards)
        const recentFinishes = [...sortedFinishes].reverse();
        for (let i = 0; i < recentFinishes.length; i++) {
          if (i === 0) {
            if (recentFinishes[i].betterThanOther) {
              currentStreak = 1;
              currentStreakType = 'winning';
            } else {
              currentStreak = 1;
              currentStreakType = 'losing';
            }
          } else {
            if ((currentStreakType === 'winning' && recentFinishes[i].betterThanOther) ||
                (currentStreakType === 'losing' && !recentFinishes[i].betterThanOther)) {
              currentStreak++;
            } else {
              break;
            }
          }
        }
        
        return {
          currentStreak,
          currentStreakType,
          longestStreak,
          isOnWinningStreak: currentStreakType === 'winning'
        };
      };

      const manager1Streaks = calculateManagerStreaks(manager1Finishes, manager1Name);
      const manager2Streaks = calculateManagerStreaks(manager2Finishes, manager2Name);

      return {
        [manager1Name]: manager1Streaks,
        [manager2Name]: manager2Streaks
      };
    };

    const streaks = calculateStreaks();

    const comparison = {
      manager1Better,
      manager2Better,
      totalYears: chartData.filter(yearData => {
        const hasManager1 = yearData.positions.some(p => p.isManager1);
        const hasManager2 = yearData.positions.some(p => p.isManager2);
        return hasManager1 && hasManager2;
      }).length
    };

    return { chartData, comparison, streaks };
  };

  const { chartData, comparison, streaks } = processStandingsData();

  // Early return if no data
  if (!chartData || !chartData.length) {
    return (
      <div className={`bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-6 lg:p-10 border border-white/20 ${className}`}>
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-2xl">
          <div className="text-center">
            <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-600">No standings data found</p>
            <p className="text-sm text-gray-500">Try adjusting your year range or manager selection</p>
          </div>
        </div>
      </div>
    );
  }

  // Tooltip handlers
  const handleMouseEnter = (e, posData, year) => {
    const rect = e.target.getBoundingClientRect();
    
    // Get both managers' standings for this year
    const yearData = chartData.find(y => y.year === year);
    const manager1Position = yearData?.positions.find(p => p.isManager1)?.position;
    const manager2Position = yearData?.positions.find(p => p.isManager2)?.position;
    
    setTooltip({
      show: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 5,
      content: {
        manager: posData.manager,
        position: posData.position,
        year: year,
        isSelected: posData.isSelected,
        manager1Position,
        manager2Position,
        manager1Name,
        manager2Name
      }
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, content: null });
  };

  return (
    <div className={`relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100 rounded-3xl shadow-2xl border border-white/50 ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/30 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-100/30 to-transparent rounded-full blur-3xl"></div>
      
      {/* Content */}
      <div className="relative p-4 sm:p-6 lg:p-12">
        {/* Header Section - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 lg:mb-12 space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl p-2 sm:p-3 shadow-lg">
              <Trophy className="h-6 w-6 sm:h-8 sm:w-8 lg:h-10 lg:w-10 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Standings Rivalry
              </h2>
              <p className="text-slate-600 text-sm sm:text-lg">Head-to-head breakdown</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{comparison.totalYears}</div>
            <div className="text-sm lg:text-base text-slate-600 font-medium">Seasons</div>
          </div>
        </div>

        {/* Manager Performance Cards - Mobile Stack */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 mb-8 lg:mb-12">
          {/* Manager 1 Card */}
          <div className="relative overflow-hidden rounded-2xl shadow-xl min-h-48 sm:min-h-64">
            {/* Background Image */}
            <div className="absolute inset-0">
              <img 
                src={`${manager1Name.toLowerCase()}${comparison.manager1Better > comparison.manager2Better ? 'happy' : comparison.manager1Better < comparison.manager2Better ? 'angry' : 'main'}.png`}
                alt={manager1Name}
                className="w-full h-full object-cover object-center scale-110"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/95 via-purple-700/90 to-purple-500/85"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            
            {/* Content */}
            <div className="relative h-full p-4 sm:p-6 lg:p-8 text-white flex flex-col justify-between min-h-48 sm:min-h-64">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className={`relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl lg:rounded-2xl overflow-hidden shadow-2xl border-2 sm:border-4 ${
                    comparison.manager1Better > comparison.manager2Better 
                      ? 'border-green-400 ring-2 sm:ring-4 ring-green-400/30' 
                      : 'border-white/40'
                  }`}>
                    <img 
                      src={`${manager1Name.toLowerCase()}${comparison.manager1Better > comparison.manager2Better ? 'happy' : comparison.manager1Better < comparison.manager2Better ? 'angry' : 'main'}.png`}
                      alt={manager1Name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-purple-500 flex items-center justify-center text-white font-bold text-lg sm:text-xl" style={{display: 'none'}}>
                      {manager1Name.charAt(0)}
                    </div>
                    {comparison.manager1Better > comparison.manager2Better && (
                      <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-green-400 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
                        <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-green-800" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold drop-shadow-lg mb-1">{manager1Name}</h3>
                    <p className="text-purple-100 text-xs sm:text-sm font-medium"></p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-2 sm:space-y-4">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold drop-shadow-lg mb-1 sm:mb-2">{comparison.manager1Better}</div>
                  <div className="text-purple-100 font-medium text-sm sm:text-base">Better Finishes</div>
                </div>
                
                {/* Streak Info - Compact on Mobile */}
                {streaks && streaks[manager1Name] && (
                  <div className="space-y-2 sm:space-y-3">
                    {/* Current Streak */}
                    <div className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-2 rounded-full backdrop-blur-sm border text-xs sm:text-sm font-medium ${
                      streaks[manager1Name].isOnWinningStreak 
                        ? 'bg-green-400/20 border-green-300/30 text-green-100' 
                        : 'bg-red-400/20 border-red-300/30 text-red-100'
                    }`}>
                      <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="sm:hidden">
                        {streaks[manager1Name].currentStreak} {streaks[manager1Name].isOnWinningStreak ? '↑' : '↓'}
                      </span>
                      <span className="hidden sm:inline">
                        Current: {streaks[manager1Name].currentStreak} {streaks[manager1Name].isOnWinningStreak ? 'better' : 'worse'}
                      </span>
                    </div>
                    
                    {/* Manager's Personal Best */}
                    <div className="text-purple-100 text-xs sm:text-sm font-medium">
                      🏆 Best: {streaks[manager1Name].longestStreak} <span className="hidden sm:inline">consecutive higher finishes</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Manager 2 Card - Similar mobile optimizations */}
          <div className="relative overflow-hidden rounded-2xl shadow-xl min-h-48 sm:min-h-64">
            {/* Background Image */}
            <div className="absolute inset-0">
              <img 
                src={`${manager2Name.toLowerCase()}${comparison.manager2Better > comparison.manager1Better ? 'happy' : comparison.manager2Better < comparison.manager1Better ? 'angry' : 'main'}.png`}
                alt={manager2Name}
                className="w-full h-full object-cover object-center scale-110"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-blue-700/90 to-blue-500/85"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            
            {/* Content */}
            <div className="relative h-full p-4 sm:p-6 lg:p-8 text-white flex flex-col justify-between min-h-48 sm:min-h-64">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className={`relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl lg:rounded-2xl overflow-hidden shadow-2xl border-2 sm:border-4 ${
                    comparison.manager2Better > comparison.manager1Better 
                      ? 'border-green-400 ring-2 sm:ring-4 ring-green-400/30' 
                      : 'border-white/40'
                  }`}>
                    <img 
                      src={`${manager2Name.toLowerCase()}${comparison.manager2Better > comparison.manager1Better ? 'happy' : comparison.manager2Better < comparison.manager1Better ? 'angry' : 'main'}.png`}
                      alt={manager2Name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg sm:text-xl" style={{display: 'none'}}>
                      {manager2Name.charAt(0)}
                    </div>
                    {comparison.manager2Better > comparison.manager1Better && (
                      <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-6 h-6 sm:w-8 sm:h-8 bg-green-400 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
                        <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-green-800" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold drop-shadow-lg mb-1">{manager2Name}</h3>
                    <p className="text-blue-100 text-xs sm:text-sm font-medium"></p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-2 sm:space-y-4">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold drop-shadow-lg mb-1 sm:mb-2">{comparison.manager2Better}</div>
                  <div className="text-blue-100 font-medium text-sm sm:text-base">Better Finishes</div>
                </div>
                
                {/* Streak Info - Compact on Mobile */}
                {streaks && streaks[manager2Name] && (
                  <div className="space-y-2 sm:space-y-3">
                    {/* Current Streak */}
                    <div className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-2 rounded-full backdrop-blur-sm border text-xs sm:text-sm font-medium ${
                      streaks[manager2Name].isOnWinningStreak 
                        ? 'bg-green-400/20 border-green-300/30 text-green-100' 
                        : 'bg-red-400/20 border-red-300/30 text-red-100'
                    }`}>
                      <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="sm:hidden">
                        {streaks[manager2Name].currentStreak} {streaks[manager2Name].isOnWinningStreak ? '↑' : '↓'}
                      </span>
                      <span className="hidden sm:inline">
                        Current: {streaks[manager2Name].currentStreak} {streaks[manager2Name].isOnWinningStreak ? 'better' : 'worse'}
                      </span>
                    </div>
                    
                    {/* Manager's Personal Best */}
                    <div className="text-blue-100 text-xs sm:text-sm font-medium">
                      🏆 Best: {streaks[manager2Name].longestStreak} <span className="hidden sm:inline">consecutive higher finishes</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Standings Chart */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-white/50">
          <div className="flex items-center justify-center mb-4 sm:mb-6 lg:mb-8">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800">Season-by-Season Breakdown</h3>
          </div>
          
          {/* Mobile Scroll Indicator */}
          {isMobile && showScrollIndicator && chartData && chartData.length > 3 && (
            <div className="flex items-center justify-center mb-4 animate-pulse">
              <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
                <ChevronLeft className="w-4 h-4 text-blue-600 animate-bounce-horizontal" />
                <span className="text-xs text-blue-700 font-medium">Swipe to explore years</span>
                <ChevronRight className="w-4 h-4 text-blue-600 animate-bounce-horizontal" />
              </div>
            </div>
          )}
          
          {/* Chart Container */}
          <div className="overflow-x-auto" onScroll={() => setShowScrollIndicator(false)}>
            <div className="min-w-full" style={{ minWidth: `${(chartData?.length || 0) * (isMobile ? 50 : 60) + 100}px` }}>
              {/* Y-axis labels */}
              <div className="flex">
                {/* Y-axis */}
                <div className={`${isMobile ? 'w-8' : 'w-12'} flex flex-col justify-between py-4 sm:py-6`} style={{ height: isMobile ? '300px' : '400px' }}>
                  {Array.from({ length: 12 }, (_, i) => (
                    <div key={i} className={`text-xs ${isMobile ? 'sm:text-sm' : 'sm:text-sm'} text-slate-600 text-right pr-1 sm:pr-2 flex items-center justify-end font-medium`} style={{ height: isMobile ? '22px' : '28px', marginTop: '-2px' }}>
                      #{i + 1}
                    </div>
                  ))}
                </div>
                
                {/* Chart area */}
                <div className="flex-1 relative" style={{ height: isMobile ? '300px' : '400px' }}>
                  {/* Data columns */}
                  <div className="flex justify-between h-full px-2 sm:px-4">
                    {chartData && chartData.map((yearData, yearIndex) => {
                      // Determine which manager finished higher this year
                      const manager1Position = yearData.positions.find(p => p.isManager1)?.position;
                      const manager2Position = yearData.positions.find(p => p.isManager2)?.position;
                      
                      let segmentColor = 'bg-slate-50'; // Default neutral color
                      let borderColor = 'border-slate-200';
                      if (manager1Position && manager2Position) {
                        if (manager1Position < manager2Position) {
                          segmentColor = 'bg-gradient-to-b from-purple-100 to-purple-50'; // Manager 1 finished higher
                          borderColor = 'border-purple-200';
                        } else if (manager2Position < manager1Position) {
                          segmentColor = 'bg-gradient-to-b from-blue-100 to-blue-50'; // Manager 2 finished higher
                          borderColor = 'border-blue-200';
                        } else {
                          segmentColor = 'bg-gradient-to-b from-yellow-100 to-yellow-50'; // Tie
                          borderColor = 'border-yellow-200';
                        }
                      }
                      
                      return (
                        <div key={yearData.year} className={`flex flex-col justify-between py-4 sm:py-6 items-center rounded-lg sm:rounded-xl mx-0.5 sm:mx-1 border border-2 ${segmentColor} ${borderColor} transition-all duration-300 hover:scale-105 hover:shadow-md ${isMobile ? 'min-w-10' : ''}`}>
                          {/* Dots for each position */}
                          {yearData.positions.map((posData, posIndex) => {
                            const getDotStyle = () => {
                              if (posData.isManager1) {
                                return `bg-purple-500 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'} opacity-100 ring-1 sm:ring-2 ring-purple-200 shadow-lg`;
                              } else if (posData.isManager2) {
                                return `bg-blue-500 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'} opacity-100 ring-1 sm:ring-2 ring-blue-200 shadow-lg`;
                              } else if (posData.manager) {
                                return `bg-slate-400 ${isMobile ? 'w-2 h-2' : 'w-3 h-3'} opacity-70`;
                              } else {
                                return `bg-slate-300 ${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'} opacity-40`;
                              }
                            };

                            return (
                              <div
                                key={posIndex}
                                className={`rounded-full transition-all duration-200 hover:scale-125 cursor-pointer ${getDotStyle()}`}
                                onMouseEnter={(e) => handleMouseEnter(e, posData, yearData.year)}
                                onMouseLeave={handleMouseLeave}
                                onClick={(e) => isMobile && handleMouseEnter(e, posData, yearData.year)}
                              />
                            );
                          })}
                          
                          {/* Year label */}
                          <div className={`${isMobile ? 'text-xs' : 'text-xs'} text-slate-700 mt-2 sm:mt-3 font-bold transform -rotate-45 origin-center`}>
                            {isMobile ? `'${yearData.year.toString().slice(-2)}` : yearData.year}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legend - Mobile Optimized */}
          <div className="flex flex-wrap justify-center mt-4 sm:mt-6 lg:mt-8 gap-2 sm:gap-4 lg:gap-8">
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded-full mr-2 sm:mr-3 ring-1 sm:ring-2 ring-purple-200 shadow-sm"></div>
              <span className="text-xs sm:text-sm font-semibold text-slate-700">{manager1Name}</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-500 rounded-full mr-2 sm:mr-3 ring-1 sm:ring-2 ring-blue-200 shadow-sm"></div>
              <span className="text-xs sm:text-sm font-semibold text-slate-700">{manager2Name}</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-slate-400 rounded-full mr-2 sm:mr-3"></div>
              <span className="text-xs sm:text-sm text-slate-600">Others</span>
            </div>
            <div className="flex items-center w-full sm:w-auto justify-center mt-2 sm:mt-0">
              <div className="w-4 h-2 sm:w-5 sm:h-3 bg-gradient-to-r from-purple-100 to-purple-50 rounded mr-2 sm:mr-3 border border-purple-200"></div>
              <span className="text-xs text-slate-600 font-medium">{manager1Name} Won</span>
            </div>
            <div className="flex items-center w-full sm:w-auto justify-center mt-2 sm:mt-0">
              <div className="w-4 h-2 sm:w-5 sm:h-3 bg-gradient-to-r from-blue-100 to-blue-50 rounded mr-2 sm:mr-3 border border-blue-200"></div>
              <span className="text-xs text-slate-600 font-medium">{manager2Name} Won</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.show && tooltip.content && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="bg-white/95 backdrop-blur-sm p-3 sm:p-4 rounded-xl shadow-xl border border-slate-200 max-w-xs">
            <p className="font-bold text-slate-800 mb-2 sm:mb-3 text-sm sm:text-base">{tooltip.content.year}</p>
            
            {tooltip.content.manager1Position && tooltip.content.manager2Position ? (
              <>
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-500 rounded-full mr-1 sm:mr-2"></div>
                      <span className="font-medium text-xs sm:text-sm">{tooltip.content.manager1Name}:</span>
                    </span>
                    <span className="font-bold text-sm sm:text-lg ml-2 sm:ml-3">#{tooltip.content.manager1Position}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full mr-1 sm:mr-2"></div>
                      <span className="font-medium text-xs sm:text-sm">{tooltip.content.manager2Name}:</span>
                    </span>
                    <span className="font-bold text-sm sm:text-lg ml-2 sm:ml-3">#{tooltip.content.manager2Position}</span>
                  </div>
                </div>
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm font-medium">Winner:</span>
                    <span className="font-bold text-green-600 text-xs sm:text-sm">
                      {tooltip.content.manager1Position < tooltip.content.manager2Position 
                        ? tooltip.content.manager1Name 
                        : tooltip.content.manager2Name}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {tooltip.content.manager ? (
                  <div className="text-slate-700">
                    <span className={`font-semibold ${tooltip.content.isSelected ? 'text-slate-800' : 'text-slate-600'} text-xs sm:text-sm`}>
                      {tooltip.content.manager}
                    </span>
                    <span className="text-slate-500 ml-2 text-sm sm:text-lg">
                      #{tooltip.content.position}
                    </span>
                  </div>
                ) : (
                  <div className="text-slate-500 text-xs sm:text-sm">
                    Position #{tooltip.content.position} - No data
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Add CSS for bounce animation */}
      <style jsx>{`
        @keyframes bounce-horizontal {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
        }
        .animate-bounce-horizontal {
          animation: bounce-horizontal 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default StandingsComparison;